// src/components/inputs/FormInput.jsx

import React, { forwardRef } from 'react';
import {
    InputLabel,
    Typography,
    TextField,
    Box,
    CardMedia,
    InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckCircle, Error } from '@mui/icons-material';
import i18next from 'i18next';

// Import custom components
import PhoneInput from './PhoneInput';
import PasswordInput from './PasswordInput';
import InputFileUpload from './InputFileUpload';
import MultiImageUpload from './MultiImageUpload';
import MultiImageUploader from './MultiImageUploader';
import LandlineInput from './LandlineInput';
import MultipleSelect from './MultipleSelect';
import TextEditor from './TextEditor';
import TimeInput from './TimeInput';
import DateInput from './DateInput';

// Import utilities
import { countries } from '../../constants/countres';
import linkIcon from './../../assets/icons/Vector.png';
import SelectAutoComplacent from './SelectAutoComplacent';

const FormInput = forwardRef(
    (
        {
            label,
            placeholder,
            type,
            labelImage,
            labelBioImage,
            selectedCountryCode = countries[0].phone,
            setSelectedCountryCode,
            phoneNumber,
            setPhoneNumber,
            labelBio,
            textareaRows = '150px',
            width = 120,
            borderRadius,
            sx,
            selectedValue,
            handleSelectChange,
            options = [],
            disabled = false,
            name = type,
            onChange,
            value,
            required = true,
            initialMemberIds,
            error,
            helperText,
            height = width === 120 ? 120 : 145,
            isUploading,
            loading,
            errorMessage,
            successMessage,
            // New props for time/date/multi-image
            min,
            max,
            maxImages = 10,
            maxSizeMB = 2,
            inputProps = {},
            fullPhoneNumber,
            disablePast,
            mediaId,
            onDeleted,
            creatable,
            onCreate,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();
        let lng = i18next.language;

        // Handle number input change
        const handleNumberChange = (e) => {
            let value = e.target.value
                .replace(/,/g, '.') // استبدال جميع الفواصل بنقاط أولاً
                .replace(/[^0-9.]/g, ''); // إزالة أي أحرف غير الأرقام أو النقاط

            // منع أكثر من نقطة عشرية
            const parts = value.split('.');
            if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
            }

            // منع النقطة في البداية (اختياري)
            if (value.startsWith('.')) {
                value = value.substring(1);
            }

            // إنشاء حدث معدل
            const newEvent = {
                ...e,
                target: {
                    ...e.target,
                    value: value,
                    name: e.target.name,
                },
            };

            if (onChange) {
                onChange(newEvent);
            }
        };

        // Validate link
        const validateLink = (value) => {
            const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[\w-./?%&=]*)?$/;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return urlRegex.test(value) || emailRegex.test(value);
        };

        return (
            <>
                {/* Label */}
                {label &&
                    <InputLabel
                        htmlFor={label}
                        sx={{ my: 1, fontWeight: 'bold', color: theme.palette.primary.default, textAlign: lng === 'ar' ? 'right' : 'left', direction: lng === 'ar' ? 'rtl' : 'ltr', }}
                    >
                        {label}
                        {required && <span style={{ color: 'red' }}> *</span>}
                    </InputLabel>
                }

                {/* Optional Label Bio */}
                {labelBio && (
                    <InputLabel
                        htmlFor={label}
                        sx={{
                            mb: 1,
                            fontWeight: 'bold',
                            color: theme.palette.secondary.default,
                            fontSize: '0.75rem',
                            textAlign: lng === 'ar' ? 'right' : 'left',
                        }}>
                        {labelBio}
                    </InputLabel>
                )}

                {/* ==== TIME INPUT ==== */}
                {type === 'time' ? (
                    <TimeInput
                        value={value}
                        onChange={onChange}
                        required={required}
                        disabled={disabled}
                        error={!!error}
                        helperText={error || helperText}
                        inputProps={inputProps}
                        {...rest}
                    />
                ) :

                    /* ==== DATE INPUT ==== */
                    type === 'date' ? (
                        <DateInput
                            name={name}
                            value={value}
                            onChange={onChange}
                            required={required}
                            disabled={disabled}
                            error={!!error}
                            helperText={error || helperText}
                            min={min}
                            max={max}
                            disablePast={disablePast}
                            inputProps={inputProps}
                            {...rest}
                        />
                    ) :

                        /* ==== MULTI IMAGE UPLOADER (New) ==== */
                        type === 'multi-image' ? (
                            <MultiImageUploader
                                value={value}
                                onChange={onChange}
                                maxImages={maxImages}
                                maxSizeMB={maxSizeMB}
                                required={required}
                                disabled={disabled}
                                {...rest}
                            />
                        ) :

                            /* ==== LINK INPUT ==== */
                            type === 'link' ? (
                                <TextField
                                    required={required}
                                    ref={ref}
                                    id={label}
                                    placeholder={placeholder}
                                    type="text"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    fullWidth
                                    error={!!error}
                                    helperText={error}
                                    sx={{
                                        backgroundColor: theme.palette.background.input,
                                        borderRadius: 1,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: validateLink(value) ? 'success.main' : 'divider',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: validateLink(value) ? 'success.main' : 'primary.main',
                                            },
                                        },
                                        ...sx,
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {validateLink(value) && !error ? (
                                                    <CheckCircle color="success" />
                                                ) : (
                                                    <Error color="error" />
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) :

                                /* ==== LANDLINE INPUT ==== */
                                type === 'landline' ? (
                                    <LandlineInput
                                        value={value}
                                        onChange={onChange}
                                        placeholder={placeholder}
                                        error={error}
                                        required={required}
                                        label={label}
                                    />
                                ) :

                                    /* ==== NUMBER INPUT ==== */
                                    type === 'number' ? (
                                        <input
                                            required={required}
                                            style={{
                                                backgroundColor: theme.palette.background.input,
                                                ...sx,
                                            }}
                                            id={label}
                                            placeholder={placeholder}
                                            type="text"
                                            inputMode="decimal"
                                            pattern="[0-9]*([.,][0-9]*)?"
                                            name={name}
                                            value={value}
                                            onChange={handleNumberChange}
                                            className="input"
                                        />
                                    ) :

                                        /* ==== PHONE INPUT ==== */
                                        type === 'tel' ? (
                                            <PhoneInput
                                                type={type}
                                                name={name}
                                                selectedCountryCode={selectedCountryCode}
                                                setSelectedCountryCode={setSelectedCountryCode}
                                                phoneNumber={phoneNumber}
                                                setPhoneNumber={setPhoneNumber}
                                                error={error}
                                                required={required}
                                                helperText={helperText}
                                                placeholder={placeholder}
                                                fullPhoneNumber={fullPhoneNumber}
                                            />
                                        ) :

                                            /* ==== PASSWORD INPUT ==== */
                                            type === 'password' ? (
                                                <PasswordInput
                                                    type={type}
                                                    placeholder={placeholder}
                                                    onChange={onChange}
                                                    name={name}
                                                    required={required}
                                                />
                                            ) :

                                                /* ==== URL INPUT WITH ICON ==== */
                                                type === 'url' ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            backgroundColor: theme.palette.background.input,
                                                            borderRadius: 1,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            padding: '4px 8px',
                                                            flexDirection: lng === 'ar' ? 'row' : 'row-reverse',
                                                            ...sx,
                                                        }}
                                                    >
                                                        <TextField
                                                            required={required}
                                                            ref={ref}
                                                            id={label}
                                                            placeholder={placeholder}
                                                            type="text"
                                                            name={name}
                                                            value={value}
                                                            onChange={onChange}
                                                            fullWidth
                                                            className="input"
                                                            InputProps={{
                                                                sx: {
                                                                    backgroundColor: 'transparent',
                                                                    border: 'none',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        border: 'none',
                                                                        p: 0,
                                                                    },
                                                                },
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-input': {
                                                                    paddingLeft: 0,
                                                                    textAlign: 'left',
                                                                    height: '6px',
                                                                    color: theme.palette.primary.main,
                                                                    '&::placeholder': {
                                                                        textAlign: 'left',
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                padding: '0 8px',
                                                                color: theme.palette.primary.main,
                                                                userSelect: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                flexDirection: lng === 'ar' ? 'row' : 'row-reverse',
                                                            }}
                                                        >
                                                            <CardMedia
                                                                component={'img'}
                                                                src={linkIcon}
                                                                sx={{ width: '20px', height: '20px', objectFit: 'contain' }}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                ) :

                                                    /* ==== TEXT EDITOR ==== */
                                                    type === 'editor' ? (
                                                        <TextEditor
                                                            value={value}
                                                            onChange={onChange}
                                                            placeholder={placeholder}
                                                            name={name}
                                                            minHeight={textareaRows}
                                                        />
                                                    ) :

                                                        /* ==== MESSAGE/TEXTAREA ==== */
                                                        type === 'message' || type === 'textarea' ? (
                                                            <textarea
                                                                required={required}
                                                                style={{
                                                                    backgroundColor: theme.palette.background.input,
                                                                    height: textareaRows,
                                                                    padding: '20px',
                                                                    resize: 'none',
                                                                }}
                                                                id={label}
                                                                multiline={'true'}
                                                                rows={6}
                                                                placeholder={placeholder}
                                                                name={name}
                                                                onChange={onChange}
                                                                value={value}
                                                                className="input"
                                                            />
                                                        ) :

                                                            /* ==== FILE UPLOAD (Single Image) ==== */
                                                            type === 'file' ? (
                                                                <InputFileUpload
                                                                    labelImage={labelImage}
                                                                    labelBioImage={labelBioImage}
                                                                    width={width}
                                                                    value={value}
                                                                    name={name}
                                                                    height={height}
                                                                    borderRadius={borderRadius}
                                                                    onChange={onChange}
                                                                    required={required}
                                                                    isUploading={isUploading}
                                                                    errorMessage={errorMessage}
                                                                    successMessage={successMessage}
                                                                    mediaId={mediaId}
                                                                    onDeleted={onDeleted}
                                                                />
                                                            ) :

                                                                /* ==== MULTIPLE IMAGES (Old Component) ==== */
                                                                type === 'multiple' ? (
                                                                    <MultiImageUpload
                                                                        label={label}
                                                                        value={value}
                                                                        onChange={(files) => {
                                                                            if (onChange) {
                                                                                onChange({ target: { name, files } });
                                                                            }
                                                                        }}
                                                                    />
                                                                ) :

                                                                    /* ==== SELECT WITH AUTOCOMPLETE ==== */
                                                                    type === 'select' ? (
                                                                        <SelectAutoComplacent
                                                                            name={name}
                                                                            value={value}
                                                                            options={options}
                                                                            onChange={onChange}
                                                                            disabled={disabled}
                                                                            sx={sx}
                                                                            error={error}
                                                                            helperText={helperText}
                                                                            creatable={creatable}
                                                                            onCreate={onCreate}
                                                                            loading={loading}
                                                                        />
                                                                    ) :

                                                                        /* ==== MULTI SELECT ==== */
                                                                        type === 'multi_select' ? (
                                                                            <MultipleSelect
                                                                                name={name}
                                                                                selectedValue={value}
                                                                                handleSelectChange={handleSelectChange || ((e) => onChange && onChange(e.target.value))}
                                                                                multiOptions={options}
                                                                                sx={sx}
                                                                                loading={loading}
                                                                                showChips={rest.showChips}
                                                                            />
                                                                        ) : (

                                                                            /* ==== DEFAULT TEXT INPUT ==== */
                                                                            <input
                                                                                required={required}
                                                                                ref={ref}
                                                                                style={{ backgroundColor: theme.palette.background.input, ...sx }}
                                                                                id={label}
                                                                                placeholder={placeholder}
                                                                                type="text"
                                                                                name={name}
                                                                                onChange={onChange}
                                                                                value={value}
                                                                                className="input"
                                                                                disabled={disabled}
                                                                            />
                                                                        )}

                {/* Error Message */}
                {/* {error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {error}
                    </Typography>
                )} */}
            </>
        );
    }
);

export default FormInput;
